// 発注済み（注文済み）の発注書一覧＋選択入荷（Stocky の Receive selected 相当）。
// 「Shopifyの在庫に反映する」が ON のときだけ入荷数量を Shopify の在庫へ加算する
// （write_inventory）。OFF なら UreyukiBox 内の記録のみ＝二重計上防止の逃げ道。
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { listLocations } from "../api/locations";
import { ApiError } from "../api/client";
import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";
import { toLocationSelectOptions } from "../utils/locationOptions";
import { getMe, updateReceiptDefaults } from "../api/me";
import {
  cancelPlacedOrder,
  listPlacedOrders,
  receivePlacedItems,
  type PlacedOrder,
  type PlacedOrderItem,
  type PlacedOrderStatus,
} from "../api/purchase_order_lifecycle";
import {
  BarcodeLabelResultBanner,
  type BarcodeLabelReport,
} from "../components/BarcodeLabelResultBanner";
import { GatedQueryError } from "../components/GatedQueryError";
import { LineSortSelect } from "../components/LineSortSelect";
import { LandedCostPanel } from "../components/po/LandedCostPanel";
import { PlacedOrderEditPanel } from "../components/po/PlacedOrderEditPanel";
import { ScanInput } from "../components/ScanInput";
import {
  countBarcodeLabels,
  type BarcodeLabelItem,
} from "../utils/barcodeLabels";
import { sortLines, type LineSortKey } from "../utils/lineSort";
import { printBarcodeLabels } from "../utils/printBarcodeLabels";
import { parseQuantityInput } from "../utils/quantityInput";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";
import { variantMatchLabel } from "../api/variants";
import type { StockyPublicBigIntId } from "../api/stocky_public_ids";

import { currentLocale, t } from "../lib/i18n";
import { useLocationLimit } from "../lib/useLocationLimit";

const QUERY_KEY = ["placed-orders"];
const RECEIPT_ATTEMPT_SCOPE = "purchase-order:receive";

// 打鍵をまとめてから問い合わせる（PurchaseOrders の商品検索と同値）。
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_LABEL: Record<PlacedOrderStatus, string> = {
  draft: "下書き",
  placed: "入荷待ち",
  partially_received: "一部入荷",
  received: "入荷済み",
  cancelled: "キャンセル",
};

const STATUS_TONE: Record<
  PlacedOrderStatus,
  "attention" | "warning" | "success" | undefined
> = {
  draft: undefined,
  placed: "attention",
  partially_received: "warning",
  received: "success",
  cancelled: undefined,
};

function isDefinitiveClientFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  // API契約上、副作用前の入力拒否と断定できるstatusだけをclearする。
  // 認証・認可・競合・rate-limit・未分類4xxは結果が曖昧なので保持する。
  return [400, 404, 422].includes(error.status);
}

/** 発注書1枚分の入荷入力（item_id → 数量文字列）。 */
type ReceiptInputs = Record<string, string>;

/**
 * スキャンで解決した商品に対応する「まだ入荷できる」明細を探す。
 *
 * 🔑 突合キーは **SKU**。発注明細は variant の内部 id しか持たず
 * `shopify_variant_id` を返していないためだが、明細の SKU は発注時に variant から
 * 写したものなので実運用では一致する。SKU 未設定の商品だけは構造的に引けないので、
 * 「発注書に無い」ではなく専用の理由を返す（嘘の説明をしない）。
 */
function findReceivableLine(
  po: PlacedOrder,
  variant: { sku: string | null },
): PlacedOrderItem | "not-in-po" | "no-sku" | "fully-received" {
  if (!variant.sku) return "no-sku";
  const matches = po.items.filter((item) => item.sku === variant.sku);
  if (matches.length === 0) return "not-in-po";
  const receivable = matches.find((item) => item.remaining_qty > 0);
  return receivable ?? "fully-received";
}

/**
 * 入荷数の入力を読む。読めた明細と**読めなかった明細の件数**を両方返す。
 *
 * 🔴 旧実装は読めない行を黙って `continue` し、ボタンは1行でも読めれば有効だった
 * ＝「5」と「3.5」を入れると 5 だけが登録され、3.5 の行は何の説明もなく残る
 * （利用者は登録できたつもりで残数のズレに後から気づく）。
 */
function parseReceipts(po: PlacedOrder, inputs: ReceiptInputs) {
  const items: {
    purchase_order_item_id: string;
    quantity: number;
  }[] = [];
  let invalidCount = 0;
  for (const item of po.items) {
    const parsed = parseQuantityInput(inputs[item.id] ?? "");
    if (parsed.kind === "blank") continue;
    if (parsed.kind === "invalid" || parsed.value <= 0) {
      invalidCount += 1;
      continue;
    }
    items.push({ purchase_order_item_id: item.id, quantity: parsed.value });
  }
  return { items, invalidCount };
}

/** 並べ替えのために明細を SKU・商品名・数量へ落とす（数量＝発注数）。 */
function toSortableItem(item: PlacedOrderItem) {
  return { sku: item.sku, title: item.title, qty: item.ordered_qty };
}

/**
 * バーコードラベルを何枚ずつ出すかを決める。
 *
 * 🔑 荷物が届いた瞬間がラベルのいちばん要る時なので、**いま手元にある数**に合わせる:
 * ①入荷数を入力済みならその数量（＝これから入荷登録する分にそのまま貼る）
 * ②未入力なら残数（＝これから入ってくる分）③残数も0なら発注数（＝貼り直しの再印刷）。
 *
 * 🔴 どの規則が効いたかはボタンに枚数として出す＝押すまで分からない隠れた規則にしない。
 */
function labelItemsFor(
  items: PlacedOrderItem[],
  entered: Map<string, number>,
): BarcodeLabelItem[] {
  const hasEntered = entered.size > 0;
  const hasRemaining = items.some((item) => item.remaining_qty > 0);

  return items.map((item) => ({
    sku: item.sku,
    barcode: item.barcode,
    title: item.title,
    qty: hasEntered
      ? (entered.get(item.id) ?? 0)
      : hasRemaining
        ? item.remaining_qty
        : item.ordered_qty,
  }));
}

export function PlacedOrders() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { shopGeneration } = useVerifiedShopGeneration();
  const [inputsByPo, setInputsByPo] = useState<Record<string, ReceiptInputs>>(
    {},
  );
  // 入荷登録時に Shopify の在庫（available）へ差分反映するか。反映先は下の
  // targetLocationId（単一拠点＝その拠点／複数拠点＝Business のみ選択可）。
  // 🔴 既定 OFF＝安全側。Shopify 側でも受領処理をする店で ON だと在庫が二重計上
  // され、実在庫が黙って狂う（気づきにくく被害が大きい）。一気通貫にしたい店が
  // 明示的にオンにし、その選択は店単位で保存する（/api/me の receipt_defaults）。
  // この画面での操作を override に持ち、未操作なら保存値を使う＝保存値が後から
  // 届いても取りこぼさず、useEffect での同期も要らない。
  const [applyToShopifyOverride, setApplyToShopifyOverride] = useState<
    boolean | null
  >(null);
  // 在庫を書き込む先の拠点。null＝未選択（主拠点にフォールバック）。
  const [receiptLocationId, setReceiptLocationId] =
    useState<StockyPublicBigIntId | null>(null);
  // 直近のラベル印刷の結果。どの発注書から出したかを持ち、そのカードの中だけに出す
  // （画面に発注書が複数並ぶので、上部に出すとどれの結果か分からない）。
  const [labelResult, setLabelResult] = useState<{
    poId: string;
    report: BarcodeLabelReport;
  } | null>(null);
  // 確定後の編集モード（カード単位）。編集中は入荷入力を隠す＝「数量」の意味が
  // 2つ（発注数の修正と入荷数の入力）同時に画面へ出て取り違えるのを防ぐ。
  const [editingPoIds, setEditingPoIds] = useState<Record<string, boolean>>({});
  const receiptAttempts = useRef<
    Record<string, PersistentIdempotencyAttempt>
  >({});

  const locationLimit = useLocationLimit();
  const canMultiLocation =
    locationLimit === undefined ||
    locationLimit.limit === null ||
    locationLimit.limit > 1;

  // 検索欄の生の入力と、実際に問い合わせる語を分ける。打鍵ごとに queryKey を
  // 変えると毎打鍵で新規クエリになり、一覧が消えては戻る（PurchaseOrders と同方式）。
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // 明細の並び。仕入先の請求書と突合するために、手元の並びを相手の書類へ寄せる。
  const [sortKey, setSortKey] = useState<LineSortKey>("default");
  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const ordersQuery = useQuery({
    queryKey: [...QUERY_KEY, searchTerm],
    queryFn: () => listPlacedOrders(searchTerm),
    // 検索語を変えている間も直前の一覧を残す（毎回空にしない）。
    placeholderData: keepPreviousData,
  });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const locationsQuery = useQuery({
    queryKey: ["receipt-locations"],
    queryFn: listLocations,
    // /api/locations は Business ゲート（403）。未満では取得しない。
    enabled: canMultiLocation,
  });

  const applyToShopify =
    applyToShopifyOverride ??
    meQuery.data?.receipt_defaults?.apply_to_shopify ??
    false;

  const primaryLocationId = meQuery.data?.locations.primary_location_id ?? null;
  const primaryLocationName = meQuery.data?.locations.primary_name ?? null;
  const isMultiLocation = (meQuery.data?.locations.count ?? 0) >= 2;

  // 🔴 入荷は「どの拠点に物が入ったか」で反映先が決まる。主拠点固定にすると
  // 複数拠点の店で実在庫と違う拠点に加算され、Shopify のデータが黙って狂う。
  // 複数拠点で反映先を選べるのは Business（複数拠点は Business 看板機能で
  // /api/locations 自体が 403）＝選べない店では在庫書き込みを許可しない。
  // 読み取りが主拠点固定なのは表示の話だが、書き込みは取り返しがつかない。
  const canChooseLocation = isMultiLocation && canMultiLocation;
  const targetLocationId = canChooseLocation
    ? (receiptLocationId ?? primaryLocationId)
    : primaryLocationId;
  const canApplyToShopify =
    targetLocationId !== null && (!isMultiLocation || canMultiLocation);

  const locationOptions = toLocationSelectOptions(locationsQuery.data ?? []);
  const targetLocationName = canChooseLocation
    ? (locationOptions.find((o) => o.value === String(targetLocationId))
        ?.label ?? primaryLocationName)
    : primaryLocationName;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    // 入荷・キャンセルで open qty が変わる＝発注提案の数量も変わる。
    queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
  };

  const receiveMutation = useMutation({
    mutationFn: (args: {
      poId: string;
      items: {
        purchase_order_item_id: string;
        quantity: number;
      }[];
      applyToShopify: boolean;
      locationId: StockyPublicBigIntId | null;
      attempt: PersistentIdempotencyAttempt;
    }) =>
      receivePlacedItems(args.poId, args.items, {
        applyToShopify: args.applyToShopify,
        locationId: args.locationId,
        idempotencyKey: args.attempt.key,
      }),
    onSuccess: (_data, args) => {
      clearPersistentIdempotencyAttempt(
        RECEIPT_ATTEMPT_SCOPE,
        args.attempt,
      );
      delete receiptAttempts.current[args.poId];
      setInputsByPo((prev) => ({ ...prev, [args.poId]: {} }));
      invalidate();
    },
    onError: (error, args) => {
      if (!isDefinitiveClientFailure(error)) return;
      clearPersistentIdempotencyAttempt(
        RECEIPT_ATTEMPT_SCOPE,
        args.attempt,
      );
      delete receiptAttempts.current[args.poId];
    },
    // Idempotency-Key があるので、応答喪失・一時的な5xxだけ1回安全に再試行できる。
    // 入力不備/競合(4xx)は同じ要求を繰り返しても直らないため再試行しない。
    retry: (failureCount, error) =>
      failureCount < 1 && (!(error instanceof ApiError) || error.status >= 500),
    retryDelay: 250,
  });

  const submitReceipt = (
    poId: string,
    items: {
      purchase_order_item_id: string;
      quantity: number;
    }[],
  ) => {
    const shouldApply = applyToShopify && canApplyToShopify;
    const locationId = shouldApply ? targetLocationId : null;
    const fingerprint = JSON.stringify({
      poId,
      items,
      applyToShopify: shouldApply,
      locationId,
    });
    const attempt = persistentIdempotencyAttempt({
      scope: RECEIPT_ATTEMPT_SCOPE,
      shopGeneration,
      previous: receiptAttempts.current[poId] ?? null,
      fingerprint,
    });
    receiptAttempts.current[poId] = attempt;
    receiveMutation.mutate({
      poId,
      items,
      applyToShopify: shouldApply,
      locationId,
      attempt,
    });
  };

  // チェックの既定を店単位で覚える。保存に失敗してもこの画面での選択は生かし、
  // 「覚えられなかった」ことだけを伝える（黙って握り潰さない）。
  const receiptDefaultsMutation = useMutation({
    mutationFn: (next: boolean) =>
      updateReceiptDefaults({ apply_to_shopify: next }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["me"] }),
  });

  const handleApplyToShopifyChange = (next: boolean) => {
    setApplyToShopifyOverride(next);
    receiptDefaultsMutation.mutate(next);
  };

  const cancelMutation = useMutation({
    mutationFn: (poId: string) => cancelPlacedOrder(poId),
    onSuccess: invalidate,
  });

  // 荷物が丸ごと届いたときが最も多い＝1件ずつ打たせない。値を入れるだけで
  // 送信はしない（確認してから「入荷登録」を押す2段は崩さない）。
  const fillRemaining = (po: PlacedOrder) => {
    setInputsByPo((prev) => {
      const next: ReceiptInputs = { ...(prev[po.id] ?? {}) };
      for (const item of po.items) {
        if (item.remaining_qty > 0) next[item.id] = String(item.remaining_qty);
      }
      return { ...prev, [po.id]: next };
    });
  };

  const handlePrintBarcodeLabels = (
    po: PlacedOrder,
    items: PlacedOrderItem[],
  ) => {
    const inputs = inputsByPo[po.id] ?? {};
    const entered = new Map(
      parseReceipts(po, inputs).items.map((r) => [
        r.purchase_order_item_id,
        r.quantity,
      ]),
    );
    const report = printBarcodeLabels(labelItemsFor(items, entered), {
      title: po.supplier_name
        ? t("{poNumber}（{supplier}）", {
            poNumber: po.po_number,
            supplier: po.supplier_name,
          })
        : po.po_number,
    });
    setLabelResult({ poId: po.id, report });
  };

  const orders = ordersQuery.data?.purchase_orders ?? [];
  const isSearching = searchTerm.trim() !== "";
  // 🔴 検索で0件になっても検索欄は消さない＝消すと検索語を取り消せなくなる。
  // 出さないのは「発注書が1枚も無い店」だけ（絞る対象そのものが無い）。
  const showSearch = orders.length > 0 || isSearching;
  const isEmpty =
    !ordersQuery.isLoading && !ordersQuery.isError && orders.length === 0;

  const clearSearch = () => {
    setSearch("");
    setSearchTerm("");
  };

  return (
    <Page
      title={t("発注済み・入荷")}
      subtitle={t(
        "「注文済みとしてマーク」で登録した発注書の入荷を記録します。入荷待ちの数量は発注提案から自動で差し引かれます",
      )}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {ordersQuery.isError && (
              <GatedQueryError
                title={t("発注書の取得に失敗しました")}
                onRetry={() => void ordersQuery.refetch()}
              />
            )}

            {(receiveMutation.isError || cancelMutation.isError) && (
              <Banner tone="critical" title={t("操作に失敗しました")}>
                <p>
                  {
                    ((receiveMutation.error ?? cancelMutation.error) as Error)
                      .message
                  }
                </p>
              </Banner>
            )}

            {ordersQuery.isLoading && (
              <Card>
                <SkeletonBodyText lines={6} />
              </Card>
            )}

            {showSearch && (
              <Card>
                <BlockStack gap="300">
                  <TextField
                    label={t("発注書を検索")}
                    value={search}
                    onChange={setSearch}
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={clearSearch}
                    placeholder={t("PO番号・仕入先名・商品名・SKU")}
                    helpText={t(
                      "入力した語を含む発注書を、この画面に出ていない古いものからも探します。",
                    )}
                  />
                  <LineSortSelect
                    value={sortKey}
                    onChange={setSortKey}
                    helpText={t(
                      "仕入先の請求書と同じ並びにすると、行を1つずつ突き合わせられます。印刷するラベルの順番も揃います。",
                    )}
                  />
                </BlockStack>
              </Card>
            )}

            {!ordersQuery.isLoading &&
              !ordersQuery.isError &&
              orders.length > 0 && (
                <Card>
                  <BlockStack gap="300">
                    <Checkbox
                      label={
                        !isMultiLocation && targetLocationName
                          ? t("Shopifyの在庫に反映する（{location}）", {
                              location: targetLocationName,
                            })
                          : t("Shopifyの在庫に反映する")
                      }
                      helpText={t(
                        "入荷登録した数量を Shopify の在庫へ加算します。Shopify 側でも同じ入荷を処理する場合はオフにしてください（二重計上防止）。",
                      )}
                      checked={applyToShopify && canApplyToShopify}
                      disabled={!canApplyToShopify}
                      onChange={handleApplyToShopifyChange}
                    />

                    {receiptDefaultsMutation.isError && (
                      <Banner
                        tone="warning"
                        title={t("この設定を記憶できませんでした")}
                      >
                        <p>
                          {t(
                            "次に開いたときは元の設定に戻ります。この画面での選択はそのまま有効なので、入荷登録はこのまま進められます。",
                          )}
                        </p>
                      </Banner>
                    )}

                    {applyToShopify && canChooseLocation && (
                      <Select
                        label={t("反映先の拠点")}
                        helpText={t(
                          "実際に商品が入った拠点を選んでください。ここで選んだ拠点の在庫に加算されます。",
                        )}
                        options={locationOptions}
                        value={
                          targetLocationId !== null
                            ? String(targetLocationId)
                            : ""
                        }
                        onChange={setReceiptLocationId}
                        disabled={locationsQuery.isLoading}
                      />
                    )}

                    {isMultiLocation && !canMultiLocation && (
                      <Banner
                        tone="info"
                        title={t("複数拠点のため在庫への反映は使えません")}
                      >
                        <p>
                          {t(
                            "このストアには拠点が複数あります。どの拠点に入荷したかを選べないと別の拠点の在庫を増やしてしまうため、反映先を選べる Business プランでのみ利用できます。入荷の記録（発注提案からの差し引き）は現在のプランでもそのまま使えます。",
                          )}
                        </p>
                      </Banner>
                    )}
                  </BlockStack>
                </Card>
              )}

            {/* 🔴 「0件」の理由を取り違えさせない＝検索で外れたのか、そもそも
                1枚も無いのかで、次にやることが正反対になる。 */}
            {isEmpty && isSearching && (
              <Banner
                tone="info"
                title={t("「{q}」に一致する発注書はありませんでした", {
                  q: searchTerm,
                })}
                action={{ content: t("検索を消す"), onAction: clearSearch }}
              >
                <p>
                  {t(
                    "PO番号・仕入先名・商品名・SKU の一部で探せます。まだ「注文済みとしてマーク」していない発注書はこの画面に並びません。",
                  )}
                </p>
              </Banner>
            )}

            {isEmpty && !isSearching && (
              <Banner
                tone="info"
                title={t("発注済みの発注書はまだありません")}
                action={{
                  content: t("注文書を作成する"),
                  onAction: () => navigate("/purchase-orders"),
                }}
              >
                <p>
                  {t(
                    "「注文書を作成」で内容を確認し「注文済みとしてマーク」を押すと、ここに並び入荷を記録できるようになります。",
                  )}
                </p>
              </Banner>
            )}

            {orders.map((po) => {
              const inputs = inputsByPo[po.id] ?? {};
              const { items: receipts, invalidCount: invalidReceiptCount } =
                parseReceipts(po, inputs);
              const isOpen =
                po.status === "placed" || po.status === "partially_received";
              const isEditing = isOpen && editingPoIds[po.id] === true;
              // 押す前に出る枚数＝押した後に実際に出る枚数（countBarcodeLabels が
              // buildBarcodeLabelSheet と一致することはテストで固定してある）。
              // 🔴 表示とラベルは同じ並びで作る＝画面の3行目と3枚目のラベルが
              // 別の商品になると、貼るときに間違える。
              const items = sortLines(
                po.items,
                sortKey,
                toSortableItem,
                currentLocale(),
              );
              const entered = new Map(
                receipts.map((r) => [r.purchase_order_item_id, r.quantity]),
              );
              const labelCount = countBarcodeLabels(
                labelItemsFor(items, entered),
              );
              // キャンセル済みは荷物が来ない＝貼る対象が無いのでラベルの話をしない。
              const showLabels = po.status !== "cancelled";
              const hasRemaining = po.items.some(
                (item) => item.remaining_qty > 0,
              );
              return (
                <Card key={po.id}>
                  <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="h2" variant="headingMd">
                          {/* 🔴 テンプレートリテラルで括弧を足すと t() を通らず、
                              英語UIでも全角カッコのままになる（EN スクショで発覚）。 */}
                          {po.supplier_name
                            ? t("{poNumber}（{supplier}）", {
                                poNumber: po.po_number,
                                supplier: po.supplier_name,
                              })
                            : po.po_number}
                        </Text>
                        <Badge tone={STATUS_TONE[po.status]}>
                          {t(STATUS_LABEL[po.status])}
                        </Badge>
                      </InlineStack>
                      {isOpen && (
                        <InlineStack gap="300" blockAlign="center">
                          <Button
                            variant="plain"
                            onClick={() =>
                              setEditingPoIds((prev) => ({
                                ...prev,
                                [po.id]: !isEditing,
                              }))
                            }
                          >
                            {isEditing ? t("修正を終了") : t("内容を修正")}
                          </Button>
                          <Button
                            tone="critical"
                            variant="plain"
                            onClick={() => {
                              if (
                                window.confirm(
                                  t(
                                    "{poNumber} をキャンセルしますか？（発注提案の差し引きから外れます）",
                                    {
                                      poNumber: po.po_number,
                                    },
                                  ),
                                )
                              ) {
                                cancelMutation.mutate(po.id);
                              }
                            }}
                            disabled={cancelMutation.isPending}
                          >
                            {t("発注をキャンセル")}
                          </Button>
                        </InlineStack>
                      )}
                    </InlineStack>

                    {isEditing && <PlacedOrderEditPanel po={po} />}

                    {isOpen && !isEditing && (
                      <ScanInput
                        label={t("{poNumber} の入荷をスキャン", {
                          poNumber: po.po_number,
                        })}
                        helpText={t(
                          "読み取るたびに、その明細の入荷数が 1 ずつ増えます（残数が上限）。",
                        )}
                        // 🔴 カタログで引けなくても発注書の明細と突合する＝発注後に
                        // Shopify 側で商品が消えても（終売判定）、届いた荷物は受け取れる
                        // （dev ストアの OLIVE-500 で実際に踏んだ）。
                        resolveFallback={(code) => {
                          const line = po.items.find(
                            (item) =>
                              (item.barcode !== null &&
                                item.barcode === code) ||
                              (item.sku !== null &&
                                item.sku.toLowerCase() === code.toLowerCase()),
                          );
                          if (!line) return null;
                          return {
                            shopify_variant_id: 0,
                            sku: line.sku,
                            barcode: line.barcode,
                            title: null,
                            product_title: line.title,
                          };
                        }}
                        validate={(variant) => {
                          const line = findReceivableLine(po, variant);
                          if (line === "not-in-po") {
                            return t(
                              "この発注書に含まれていない商品です（{item}）。",
                              {
                                item: variantMatchLabel(variant),
                              },
                            );
                          }
                          if (line === "no-sku") {
                            // SKU 突合なので、SKU が無い明細はスキャンで引けない。
                            // 黙って「含まれていない」と言うと嘘になるので分けて出す。
                            return `${variantMatchLabel(variant)}: ${t("この商品には SKU が設定されていないため、スキャンで照合できません。数量欄に直接入力してください。")}`;
                          }
                          if (line === "fully-received") {
                            return t(
                              "この商品は既に全量入荷済みです（{item}）。",
                              {
                                item: variantMatchLabel(variant),
                              },
                            );
                          }
                          return null;
                        }}
                        onResolved={(variant) => {
                          const line = findReceivableLine(po, variant);
                          if (typeof line === "string") return;
                          setInputsByPo((prev) => {
                            const current = prev[po.id] ?? {};
                            const parsed = Number.parseInt(
                              (current[line.id] ?? "").trim(),
                              10,
                            );
                            const base =
                              Number.isInteger(parsed) && parsed > 0
                                ? parsed
                                : 0;
                            // 残数を超えると backend が 422 を返す＝手前で止める。
                            const next = Math.min(base + 1, line.remaining_qty);
                            return {
                              ...prev,
                              [po.id]: { ...current, [line.id]: String(next) },
                            };
                          });
                        }}
                      />
                    )}

                    {!isEditing && (
                      <BlockStack gap="200">
                        {items.map((item) => (
                          <InlineGrid
                            key={item.id}
                            columns={{ xs: 1, md: "minmax(0, 1fr) auto 120px" }}
                            gap="400"
                            alignItems="center"
                          >
                            <div
                              style={{ minWidth: 0, overflowWrap: "anywhere" }}
                            >
                              <Text as="span">
                                {item.title ?? item.sku ?? t("（商品名なし）")}
                              </Text>
                              {item.sku && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                  SKU: {item.sku}
                                </Text>
                              )}
                            </div>
                            <Text as="span" variant="bodySm">
                              {t(
                                "発注 {ordered} ／ 入荷済み {received} ／ 残 {remaining}",
                                {
                                  ordered: item.ordered_qty,
                                  received: item.received_qty,
                                  remaining: item.remaining_qty,
                                },
                              )}
                            </Text>
                            {isOpen && item.remaining_qty > 0 && (
                              <BlockStack gap="100">
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {t("入荷数")}
                                </Text>
                                <TextField
                                  label={t("{item} の入荷数", {
                                    item: item.sku ?? item.id,
                                  })}
                                  labelHidden
                                  autoComplete="off"
                                  type="number"
                                  min={1}
                                  max={item.remaining_qty}
                                  placeholder={t("入荷数")}
                                  value={inputs[item.id] ?? ""}
                                  onChange={(value) =>
                                    setInputsByPo((prev) => ({
                                      ...prev,
                                      [po.id]: {
                                        ...(prev[po.id] ?? {}),
                                        [item.id]: value,
                                      },
                                    }))
                                  }
                                />
                              </BlockStack>
                            )}
                          </InlineGrid>
                        ))}
                      </BlockStack>
                    )}

                    {labelResult?.poId === po.id && (
                      <BarcodeLabelResultBanner
                        result={labelResult.report}
                        onDismiss={() => setLabelResult(null)}
                      />
                    )}

                    {(showLabels || isOpen) && !isEditing && (
                      <InlineStack align="end" gap="300" blockAlign="center">
                        {/* 🔴 「押せない」を黙って出さない＝placeholder が数字だった頃は
                            入力済みに見えるのにボタンが無効で、壊れて見えていた。 */}
                        {isOpen && receipts.length === 0 && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {t("入荷した数量を入力すると押せます。")}
                          </Text>
                        )}
                        {/* 🔴 読めなかった行は黙って捨てない＝一部だけ登録されて
                            残りが説明なく残る、を防ぐ。 */}
                        {isOpen && invalidReceiptCount > 0 && (
                          <Text as="span" variant="bodySm" tone="critical">
                            {t(
                              "{count} 件の入荷数が数値として読めません（整数で入力してください）。このままだと残りの明細だけが登録されます。",
                              { count: String(invalidReceiptCount) },
                            )}
                          </Text>
                        )}
                        {isOpen && hasRemaining && (
                          <Button onClick={() => fillRemaining(po)}>
                            {t("残りを全部入荷")}
                          </Button>
                        )}
                        {/* 🔴 1枚も出せないときはボタンを出さず理由をその場に出す。
                            押しても何も起きないボタンは、原因を探させるだけになる。 */}
                        {showLabels &&
                          (labelCount > 0 ? (
                            <Button
                              onClick={() =>
                                handlePrintBarcodeLabels(po, items)
                              }
                            >
                              {t("バーコードラベルを印刷（{n}枚）", {
                                n: labelCount,
                              })}
                            </Button>
                          ) : (
                            <Text as="span" variant="bodySm" tone="subdued">
                              {t(
                                "この発注書の商品はバーコード（JAN）が未設定のため、ラベルを印刷できません。",
                              )}
                            </Text>
                          ))}
                        {isOpen && (
                          <Button
                            variant="primary"
                            onClick={() => submitReceipt(po.id, receipts)}
                            disabled={
                              receipts.length === 0 || receiveMutation.isPending
                            }
                            loading={receiveMutation.isPending}
                          >
                            {t("入力した数量を入荷登録")}
                          </Button>
                        )}
                      </InlineStack>
                    )}

                    {/* 入荷済み・キャンセル済みでも実原価は見たい（後から諸掛の
                        請求が届く）ので isOpen で絞らない。開いたときだけ取得する。 */}
                    <LandedCostPanel poId={po.id} poNumber={po.po_number} />
                  </BlockStack>
                </Card>
              );
            })}

            {orders.length > 0 && (
              <Text as="p" variant="bodySm" tone="subdued">
                {t(
                  "「Shopifyの在庫に反映する」がオンのとき、入荷登録した数量だけを Shopify の在庫へ加算します（それ以外の場面で在庫を自動で書き換えることはありません）。入荷実績はリードタイムの統計（安全在庫の精度）にも使われます。",
                )}
                {t(
                  "バーコードラベルは、入荷数を入力していればその数量、未入力なら残数（入荷済みの発注書は発注数）ぶんを印刷します。",
                )}
              </Text>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
